import { useEffect, useState } from 'react'
import { HashRouter, BrowserRouter, useLocation, useNavigate } from 'react-router-dom'
import { useDeepLink } from './hooks/useDeepLink'
import { useConnectCloud } from './hooks/useConnectCloud'
import { useModeInitialization } from './hooks/useModeInitialization'
import { useUndoRouter } from './hooks/useUndoRouter'
import { useLastUndoShortcut } from './hooks/useLastUndoShortcut'
import { useDevReset } from './hooks/useDevReset'
import { useDocumentTitle } from './hooks/useDocumentTitle'
import { AuthProvider, useAuth } from './contexts/AuthProvider'
import { getPlatformAPI } from './lib/platform'
import { routeSendsOwnPageview, trackPageview } from './lib/analytics'
import { dispatchAppEvent } from './lib/events'
import { isStandaloneWindowRoute } from './routes/standaloneWindowRoutes'
import { migrateOfflineDataToDexie } from './lib/db/migrations'
import { convertLegacySubtasks } from './lib/db/legacySubtasks'
import { getSetupFlowProgress } from './lib/supabase/setupFlow'
import { getRouteForStep } from './lib/types/setupFlow'
import { navigateToHome } from './routes/navigateToHome'
import { WebRoutes } from './routes/WebRoutes'
import { ElectronRoutes } from './routes/ElectronRoutes'
import { UpdateDialog } from './components/layout/UpdateDialog'
import { UpdateStatusProvider } from './contexts/UpdateStatusProvider'
import { ClickSparkProvider } from './components/common/ClickSpark'
import { AppToaster } from './components/common/Toast'
import { TestDataActionsProvider } from './features/taskManagement'

// Apply theme immediately - before component even renders
const applyInitialTheme = () => {
  if (document.body.classList.contains('theme-initialized')) return

  // HARDCODED: Always use light theme for Setup/Onboarding pages
  document.body.classList.remove('dark')
  document.body.classList.add('light')
  document.body.classList.add('theme-initialized')
}

// Run immediately when module loads
applyInitialTheme()

// Meldet jeden Routenwechsel an die App (route-change) und an die Analyse.
function RouteTracker() {
  const { pathname } = useLocation()

  useEffect(() => {
    dispatchAppEvent('route-change', pathname)
    // `lastRoute` schreibt der Main-Prozess an den Fenster-Übergängen selbst (ipcEvents.ts).
    // Hier pro Route zu speichern hieße, dass auch /quick-add dort landet — und die App
    // beim nächsten Start klein statt als Hauptfenster aufginge.
    if (!routeSendsOwnPageview(pathname)) trackPageview(pathname)
  }, [pathname])

  return null
}

// Setup Flow Auto-Resume Component - CENTRAL ROUTING LOGIC
// This is the ONLY component responsible for setup flow navigation
function SetupFlowAutoResume() {
  const { isAuthenticated, loading, user } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [hasChecked, setHasChecked] = useState(false)

  useEffect(() => {
    const autoResumeSetupFlow = async () => {
      if (loading) return
      if (!isAuthenticated || !user) return
      if (hasChecked) return

      // Während eines Password-Recovery-Flows NICHT weg-navigieren — SetupPage
      // muss die Chance haben, ResetPasswordForm zu rendern. Supabase etabliert
      // beim Klick auf den Recovery-Link bereits eine Session, deshalb würden
      // wir ohne diesen Guard direkt zu /home oder /setup-profile springen.
      if (localStorage.getItem('passwordRecoveryMode') === 'true') {
        setHasChecked(true)
        return
      }

      const loginMethod = localStorage.getItem('loginMethod')
      if (loginMethod === 'offline') {
        setHasChecked(true)
        return
      }

      const isHomeRoute = pathname === '/home' ||
                          pathname.startsWith('/home/') ||
                          window.location.hash === '#/home' ||
                          window.location.hash.startsWith('#/home');
      if (isHomeRoute) {
        setHasChecked(true)
        return
      }

      const isOAuthCallback = pathname === '/auth/callback' ||
                              window.location.hash === '#/auth/callback' ||
                              window.location.hash.startsWith('#/auth/callback');
      if (isOAuthCallback) {
        setHasChecked(true)
        return
      }

      const isOnboardingRoute = pathname === '/onboarding' ||
                                window.location.hash === '#/onboarding' ||
                                window.location.hash.startsWith('#/onboarding');
      if (isOnboardingRoute) {
        setHasChecked(true)
        return
      }

      /*
        **Fenster bleiben, wo sie sind.** Dieser Wächter läuft in JEDEM
        Fenster, das das Haupt-Bundle lädt — und schickte sie bei einem
        abgeschlossenen Setup nach `/home`. Sie navigierten sich also selbst
        weg, sobald sie aufgingen.

        Hier stand bis zum 22.09.2026 nur das Fokus-Fenster. Das
        Schnell-Notieren-Fenster (und die Aufgaben-Details in einem eigenen
        Fenster) hatte dieselbe Not, aber keine Ausnahme: Es erschien kurz und
        wurde dann von der Listenansicht überschrieben. Darum jetzt EINE
        Liste statt einer Ausnahme je Fenster.
      */
      if (isStandaloneWindowRoute(pathname)) {
        setHasChecked(true)
        return
      }

      try {
        const progress = await getSetupFlowProgress()

        if (!progress) {
          navigate('/setup-profile', { replace: true })
          setHasChecked(true)
          return
        }

        if (progress.completed) {
          // Server is source of truth — rehydrate localStorage (wiped on logout)
          // so ProtectedRoute and other localStorage-based checks stay in sync.
          localStorage.setItem('onboarding_completed', 'true')
          setHasChecked(true)
          await navigateToHome(navigate)
          return
        }

        const targetRoute = getRouteForStep(progress.step)

        if (pathname === targetRoute) {
          setHasChecked(true)
          return
        }

        navigate(targetRoute, { replace: true })
        setHasChecked(true)
      } catch (error) {
        console.error('[SetupFlowAutoResume] Error:', error)
        setHasChecked(true)
      }
    }

    autoResumeSetupFlow()
  }, [isAuthenticated, loading, user, pathname, hasChecked, navigate])

  return null
}

// Deep Link Handler Wrapper Component
function DeepLinkHandler() {
  useDeepLink()
  // Bridge for the preferences-window "Mit HappyCloud verbinden" button → runs
  // the connect/login flow here in the main window. See useConnectCloud.
  useConnectCloud()
  return null
}

function App() {
  useModeInitialization()
  useDevReset()
  // ⌘Z verteilen: Unter Electron verbraucht das native Menü die Taste, die
  // Nachricht kommt von dort. Muss app-weit hängen, sonst verlöre jedes
  // Eingabefeld sein Rückgängig, sobald der Canvas zu ist.
  useUndoRouter()
  // ⌘Z in den Aufgabenlisten (F-Q1): Es nimmt zurück, was gerade angeboten
  // wird — die offene Rückgängig-Meldung oder den Haken in seiner Liegezeit.
  // Die Pinnwand belegt die Taste, solange sie offen ist, und behält ihre Kette.
  useLastUndoShortcut()
  // Reiter-Titel und Beschreibung folgen der Sprache. Nur im Web wirksam.
  useDocumentTitle()

  const platformAPI = getPlatformAPI()

  // Run migration on app start
  useEffect(() => {
    migrateOfflineDataToDexie()
    // Unteraufgaben → Aufgaben (T14 #179) — auch ohne Sync, also schon beim Start.
    convertLegacySubtasks().catch(error => console.error('[Subtasks] Umwandlung fehlgeschlagen', error))
  }, [])

  // Windows platform class for CSS corrections (DirectWrite vs CoreText font metrics)
  useEffect(() => {
    if (platformAPI.platform === 'electron' && navigator.userAgent.includes('Windows')) {
      document.documentElement.classList.add('platform-win')
    }
  }, [platformAPI.platform])

  const Router = platformAPI.platform === 'web' ? BrowserRouter : HashRouter

  return (
    <Router>
      <AuthProvider>
        <UpdateStatusProvider>
          <UpdateDialog />
          <DeepLinkHandler />
          <RouteTracker />
          <SetupFlowAutoResume />
          <ClickSparkProvider>
            <TestDataActionsProvider>
              {platformAPI.platform === 'web' ? <WebRoutes /> : <ElectronRoutes />}
            </TestDataActionsProvider>
          </ClickSparkProvider>
          {/* Toasts unten rechts, als Stapel (#223). */}
          <AppToaster />
        </UpdateStatusProvider>
      </AuthProvider>
    </Router>
  )
}

export default App
